import { RedemptionType } from '@/api/model/redemptionType';
import type { VoucherCreate } from '@/api/model/voucherCreate';
import { VoucherStatus } from '@/api/model/voucherStatus';
import { useCreateVoucher } from '@/api/vouchers/vouchers';
import { useAuth } from '@/contexts/AuthContext';
import { convertDateTimeLocalToISO } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/api-error';
import React, { useEffect } from 'react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { VoucherForm, type VoucherFormData } from './VoucherForm';

interface VoucherCreateFormProps {
  onFormDataChange?: (data: VoucherFormData) => void;
  hideImagesField?: boolean;
  onFormInstance?: (form: UseFormReturn<VoucherFormData>) => void;
}

export const VoucherCreateForm: React.FC<VoucherCreateFormProps> = ({
  onFormDataChange,
  hideImagesField = false,
  onFormInstance,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const form = useForm<VoucherFormData>({
    defaultValues: {
      title: '',
      provider_id: '',
      description: '',
      conditions: '',
      value: 0,
      valid_from: '',
      valid_until: '',
      max_redemptions: undefined,
      status: VoucherStatus.DRAFT,
      redemption_type: RedemptionType.CODE,
      images: [],
    },
  });

  const createVoucherMutation = useCreateVoucher();

  const watchedData = useWatch({ control: form.control });

  // Watch for form changes and update preview
  useEffect(() => {
    if (!onFormDataChange || !watchedData) return;
    onFormDataChange(watchedData as VoucherFormData);
  }, [onFormDataChange, watchedData]);

  // Expose form instance to parent when requested
  useEffect(() => {
    if (onFormInstance) {
      onFormInstance(form);
    }
  }, [form, onFormInstance]);

  const onSubmit = (data: VoucherFormData) => {
    if (!user?.id) {
      toast.error('Benutzer nicht authentifiziert');
      return;
    }

    const normalizedMaxRedemptions = typeof data.max_redemptions === 'number' ? data.max_redemptions : null;

    const voucherData: VoucherCreate = {
      ...data,
      created_by: user.id,
      value: Number(data.value),
      max_redemptions: normalizedMaxRedemptions,
      valid_from: convertDateTimeLocalToISO(data.valid_from),
      valid_until: convertDateTimeLocalToISO(data.valid_until),
      images: data.images && data.images.length > 0 ? data.images : undefined,
    };

    createVoucherMutation
      .mutateAsync({ data: voucherData })
      .then(() => {
        toast.success('Gutschein erfolgreich erstellt');
        navigate('/admin/vouchers');
      })
      .catch(error => {
        toast.error(getApiErrorMessage(error, 'Erstellen des Gutscheins fehlgeschlagen'));
      });
  };

  const handleCancel = () => {
    navigate('/admin/vouchers');
  };

  return (
    <VoucherForm
      form={form}
      onSubmit={onSubmit}
      isSubmitting={createVoucherMutation.isPending}
      submitText='Gutschein erstellen'
      onCancel={handleCancel}
      cancelText='Abbrechen'
      showImagesField={!hideImagesField}
    />
  );
};
